/**
 * Native AJAX cart — drop-in replacement for liquid-ajax-cart v2, implemented as
 * a single self-contained custom element: <native-ajax-cart>.
 *
 * ALL cart logic lives inside the element class (state, requests, bundled section
 * rendering, binders, product forms, quantity controls, request buttons). The
 * element is a page-lifetime singleton mounted once in layout/theme.liquid; on
 * connect it publishes the same public contract the theme already depends on:
 *
 *   window.liquidAjaxCart.add/change/update/clear/get/conf  (+ .cart, .processing)
 *   events: liquid-ajax-cart:request-start | :request-end | :init
 *   event.detail.requestState = { requestType, requestBody, responseData:{ok,status,body}, info, sections }
 *   options = { info:{initiator}, firstCallback, lastCallback }
 *   attributes: data-ajax-cart-section[/-scroll], data-ajax-cart-bind="item_count",
 *               data-ajax-cart-quantity-minus/plus/input, data-ajax-cart-request-button,
 *               data-ajax-cart-static-element, data-ajax-cart-initial-state,
 *               <ajax-cart-product-form>, html.js-ajax-cart-empty/-not-empty
 *
 * Built on Shopify's native Cart AJAX API + bundled section rendering:
 * https://shopify.dev/docs/api/ajax/reference/cart
 */
(function() {
  const cartEvents = {
    start: 'liquid-ajax-cart:request-start',
    end: 'liquid-ajax-cart:request-end',
    init: 'liquid-ajax-cart:init',
  };

  const defaultConf = {
    updateOnWindowFocus: false,
    quantityDebounce: 500,
  };

  function isFullCart(value) {
    return Boolean(value && Array.isArray(value.items) && typeof value.total_price === 'number');
  }

  class NativeAjaxCart extends HTMLElement {
    connectedCallback() {
      // Page-lifetime singleton: ignore any duplicate mounts.
      if (window.__nativeAjaxCart) { return; }
      window.__nativeAjaxCart = this;

      this._conf = {...defaultConf};
      this._cart = this.readInitialCart();
      this._processing = false;
      this._qtyTimers = new Map();

      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onSubmit = this._onSubmit.bind(this);

      document.addEventListener('click', this._onClick);
      document.addEventListener('change', this._onChange);
      // Capture phase: intercept product-form submits before native navigation.
      document.addEventListener('submit', this._onSubmit, true);

      window.addEventListener('pageshow', (event) => {
        if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
          this.update({}, {});
        }
      });

      // Publish the liquid-ajax-cart-compatible API.
      window.liquidAjaxCart = this;

      if (isFullCart(this._cart)) { this.updateBinders(this._cart); }

      this.dispatch(cartEvents.init, {
        requestType: 'init',
        responseData: {ok: true, body: this._cart},
      });
    }

    /* ── Public API (liquid-ajax-cart compatible) ────────────────────────── */

    get cart() { return this._cart; }
    get processing() { return this._processing; }

    add(body, options) { return this.request('add', body, options); }
    change(body, options) { return this.request('change', body, options); }
    update(body, options) { return this.request('update', body, options); }
    clear(options) { return this.request('clear', {}, options); }

    get(callback) {
      return this.fetchCart().then((cartData) => {
        this.updateBinders(cartData);
        if (typeof callback === 'function') {
          callback({responseData: {ok: true, body: cartData}});
        }
        return cartData;
      });
    }

    conf(key, value) {
      if (typeof key === 'object') {
        Object.assign(this._conf, key);
        return this._conf;
      }
      if (value === undefined) { return this._conf[key]; }
      this._conf[key] = value;
      return value;
    }

    /* ── Helpers ─────────────────────────────────────────────────────────── */

    root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    readInitialCart() {
      const el = document.querySelector('[data-ajax-cart-initial-state]');
      if (!el) { return null; }
      try {
        const parsed = JSON.parse(el.textContent || '');
        return isFullCart(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }

    dispatch(name, requestState, cart) {
      const detailCart = cart === undefined ? this._cart : cart;
      document.dispatchEvent(
        new CustomEvent(name, {detail: {requestState, cart: detailCart}}),
      );
    }

    // GET /cart.js (deduped by the global fetch patch in theme.liquid).
    fetchCart() {
      return fetch(`${this.root()}cart.js`, {headers: {Accept: 'application/json'}})
        .then((res) => res.json())
        .then((cartData) => {
          if (isFullCart(cartData)) { this._cart = cartData; }
          return cartData;
        });
    }

    /* ── Section rendering ───────────────────────────────────────────────── */

    // Map of { sectionId -> [liveElements] } for every cart section in the DOM.
    sectionRegistry() {
      const map = new Map();
      document.querySelectorAll('[data-ajax-cart-section]').forEach((el) => {
        const wrap = el.closest('[id^="shopify-section-"]');
        if (!wrap) { return; }
        const id = wrap.id.replace('shopify-section-', '');
        if (!map.has(id)) { map.set(id, []); }
        map.get(id).push(el);
      });
      return map;
    }

    uniqueSectionIds() {
      return Array.from(this.sectionRegistry().keys());
    }

    // Preserve [data-ajax-cart-static-element] live nodes (client-populated UI
    // like the samples block / tiered-GWP bar) across an innerHTML swap so their
    // JS state survives. Also restores scroll position of the scroll container.
    swapPreservingStatic(liveEl, freshEl) {
      const liveStatics = new Map();
      liveEl.querySelectorAll('[data-ajax-cart-static-element]').forEach((node) => {
        liveStatics.set(node.getAttribute('data-ajax-cart-static-element'), node);
      });

      const liveScroll = liveEl.matches('[data-ajax-cart-section-scroll]')
        ? liveEl
        : liveEl.querySelector('[data-ajax-cart-section-scroll]');
      const scrollTop = liveScroll ? liveScroll.scrollTop : 0;

      liveEl.innerHTML = freshEl.innerHTML;

      liveEl.querySelectorAll('[data-ajax-cart-static-element]').forEach((placeholder) => {
        const name = placeholder.getAttribute('data-ajax-cart-static-element');
        const preserved = liveStatics.get(name);
        if (preserved) { placeholder.replaceWith(preserved); }
      });

      const newScroll = liveEl.matches('[data-ajax-cart-section-scroll]')
        ? liveEl
        : liveEl.querySelector('[data-ajax-cart-section-scroll]');
      if (newScroll && scrollTop) { newScroll.scrollTop = scrollTop; }
    }

    // sectionsHtml: { sectionId: "<html>" } from bundled rendering or the Section
    // Rendering API. Swap each matching live [data-ajax-cart-section] in place.
    renderSections(sectionsHtml) {
      if (!sectionsHtml) { return; }
      const registry = this.sectionRegistry();

      Object.keys(sectionsHtml).forEach((sectionId) => {
        const html = sectionsHtml[sectionId];
        const liveList = registry.get(sectionId);
        if (!html || !liveList || !liveList.length) { return; }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshList = Array.from(doc.querySelectorAll('[data-ajax-cart-section]'));

        liveList.forEach((liveEl, i) => {
          const freshEl = freshList[i] || freshList[0];
          if (freshEl) { this.swapPreservingStatic(liveEl, freshEl); }
        });
      });
    }

    /* ── Binders + empty-state classes ───────────────────────────────────── */

    updateBinders(cartData) {
      if (!isFullCart(cartData)) { return; }
      const count = cartData.item_count || 0;

      document.querySelectorAll('[data-ajax-cart-bind="item_count"]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('showc', count > 0);
        el.classList.toggle('hidec', count === 0);
      });

      this.syncEmptyClass(count);
    }

    // html.js-ajax-cart-empty hides #tiered-gwp-messages. On the first ATC from
    // an empty cart, add.js is not a full cart so binders would otherwise wait
    // on /cart.js — leaving the free-shipping bar display:none. Use the drawer
    // DOM (already swapped via bundled sections) as the source of truth.
    syncEmptyClass(itemCount) {
      const drawerHasItems = !!document.querySelector('cart-drawer .cart-item');
      const isEmpty = drawerHasItems ? false : itemCount === 0;
      const html = document.documentElement;
      html.classList.toggle('js-ajax-cart-empty', isEmpty);
      html.classList.toggle('js-ajax-cart-not-empty', !isEmpty);
    }

    /* ── Errors ──────────────────────────────────────────────────────────── */

    clearErrors() {
      document.querySelectorAll('[data-ajax-cart-errors]').forEach((el) => {
        el.textContent = '';
      });
    }

    showError(key, message) {
      const selector = key
        ? `[data-ajax-cart-errors="${key}"]`
        : '[data-ajax-cart-errors="form"]';
      const target =
        document.querySelector(selector) || document.querySelector('[data-ajax-cart-errors]');
      if (target) { target.textContent = message || 'Something went wrong.'; }
    }

    /* ── Core request ────────────────────────────────────────────────────── */

    endpointFor(type) {
      switch (type) {
        case 'add': return `${this.root()}cart/add.js`;
        case 'change': return `${this.root()}cart/change.js`;
        case 'update': return `${this.root()}cart/update.js`;
        case 'clear': return `${this.root()}cart/clear.js`;
        default: return null;
      }
    }

    async request(type, body, options) {
      const opts = options || {};
      const url = this.endpointFor(type);
      if (!url) { throw new Error(`Unknown cart request: ${type}`); }

      const payload = {
        ...body,
        sections: this.uniqueSectionIds().join(','),
        sections_url: window.location.pathname,
      };

      const requestState = {
        requestType: type,
        requestBody: body,
        info: opts.info || {},
        responseData: null,
        sections: null,
      };

      this._processing = true;
      // Flag the whole cart as busy so other controls (qty steppers, remove,
      // request buttons) can be visually disabled for the duration — see cart.css.
      document.documentElement.classList.add('js-ajax-cart-processing');
      this.clearErrors();
      if (typeof opts.firstCallback === 'function') {
        try { opts.firstCallback(requestState); } catch { /* noop */ }
      }
      this.dispatch(cartEvents.start, requestState);

      // Full cart passed in the request-end event detail. Left null for `add`
      // (its response is only the added line item) so listeners read a fresh
      // /cart.js rather than a stale cart.
      let eventCart = null;

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify(payload),
        });
        const data = await res.json();

        // Cart errors arrive as { status, message, description } on 4xx.
        const ok = res.ok && !(data && (data.status === 422 || data.status === 404));

        requestState.responseData = {ok, status: res.status, body: data};
        requestState.sections = data && data.sections ? data.sections : null;

        if (ok) {
          // add.js bundles the cart sections, so the drawer renders here with
          // the new line in place before request-end fires (and the drawer opens).
          this.renderSections(requestState.sections);

          if (isFullCart(data)) {
            // change/update/clear return the full cart — update chrome inline.
            this._cart = data;
            eventCart = data;
            this.updateBinders(data);
          } else {
            // add.js returns only the added line item. Unhide empty-cart chrome
            // now (free-shipping bar) so the first ATC isn't stuck behind
            // html.js-ajax-cart-empty while /cart.js is still in flight.
            this.syncEmptyClass(1);
            this.fetchCart()
              .then((cartData) => {
                if (isFullCart(cartData)) { this.updateBinders(cartData); }
              })
              .catch(() => { /* noop */ });
          }
        } else {
          this.showError(
            (body && (body.id || body.line)) || 'form',
            (data && (data.description || data.message)) || 'Could not update cart.',
          );
        }
      } catch (error) {
        requestState.responseData = {
          ok: false,
          status: 0,
          body: {message: String(error)},
        };
        this.showError('form', 'Network error. Please try again.');
      } finally {
        this._processing = false;
        document.documentElement.classList.remove('js-ajax-cart-processing');
      }

      this.dispatch(cartEvents.end, requestState, eventCart);
      if (typeof opts.lastCallback === 'function') {
        try { opts.lastCallback(requestState); } catch { /* noop */ }
      }
      return requestState;
    }

    /* ── DOM event handlers (delegated) ──────────────────────────────────── */

    // <ajax-cart-product-form> wraps a product <form>. Intercept submit → add.
    _onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) { return; }
      const wrapper = form.closest('ajax-cart-product-form');
      if (!wrapper) { return; }
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) { return; }

      event.preventDefault();
      if (this._processing) { return; }

      const formData = new FormData(form);
      const item = {};
      formData.forEach((value, key) => {
        if (key === 'id') {
          item.id = value;
        } else if (key === 'quantity') {
          item.quantity = Number(value) || 1;
        } else if (key === 'selling_plan') {
          item.selling_plan = value;
        } else if (key.startsWith('properties[')) {
          const prop = key.slice('properties['.length, -1);
          item.properties = item.properties || {};
          item.properties[prop] = value;
        }
      });
      if (item.id == null) { return; }
      if (item.quantity == null) { item.quantity = 1; }

      // Match liquid-ajax-cart: the initiator is the <ajax-cart-product-form>
      // wrapper element. PDP/bundle button-state listeners resolve their button
      // via initiator.querySelector('[data-phn-std-atc]') / initiator === form,
      // so they need the wrapper (an ancestor), not the submit button itself.
      this.add({items: [item]}, {info: {initiator: wrapper}});
    }

    // Quantity steppers: data-ajax-cart-quantity-minus/plus around a
    // data-ajax-cart-quantity-input="{line}".
    _onClick(event) {
      const stepper = event.target.closest('[data-ajax-cart-quantity-minus], [data-ajax-cart-quantity-plus]');
      if (stepper) {
        // Always stop the anchor href from navigating, even when we bail out.
        event.preventDefault();
        // One cart action at a time: ignore steppers while a write is in flight
        // (prevents double-decrement races before the line re-renders).
        if (this._processing) { return; }

        const isPlus = stepper.hasAttribute('data-ajax-cart-quantity-plus');
        const scope =
          stepper.closest('ajax-cart-quantity, [data-ajax-cart-quantity], .cart-item, li, tr') || document;
        const input = scope.querySelector('[data-ajax-cart-quantity-input]');
        if (!input) { return; }

        const line = parseInt(input.getAttribute('data-ajax-cart-quantity-input'), 10);
        const current = parseInt(input.value, 10) || 0;
        // Minus never drops to 0 — at quantity 1 the line shows a dedicated
        // delete (trash) button instead, so item removal is always explicit.
        const next = isPlus ? current + 1 : Math.max(1, current - 1);
        if (next === current) { return; }
        input.value = next;
        this.change({line, quantity: next}, {info: {initiator: stepper}});
        return;
      }

      // data-ajax-cart-request-button: <a href="/cart/change?line=1&quantity=0">.
      const reqBtn = event.target.closest('[data-ajax-cart-request-button]');
      if (reqBtn) {
        const href = reqBtn.getAttribute('href');
        if (!href) { return; }
        event.preventDefault();
        if (this._processing) { return; }

        let parsed;
        try {
          parsed = new URL(href, window.location.origin);
        } catch {
          return;
        }
        const params = parsed.searchParams;
        const line = params.get('line') || reqBtn.dataset.line || reqBtn.closest('[data-line]')?.dataset.line;
        const id = params.get('id');
        const quantity = params.get('quantity');

        if (/\/cart\/change/.test(parsed.pathname)) {
          const reqBody = {quantity: quantity == null ? 0 : Number(quantity)};
          if (line != null) {
            reqBody.line = Number(line);
          } else if (id != null) {
            reqBody.id = id;
          }
          this.change(reqBody, {info: {initiator: reqBtn}});
        } else if (/\/cart\/clear/.test(parsed.pathname)) {
          this.clear({info: {initiator: reqBtn}});
        }
      }
    }

    // Debounced quantity input typing → change.js.
    _onChange(event) {
      const input = event.target.closest('[data-ajax-cart-quantity-input]');
      if (!input) { return; }
      if (this._processing) { return; }
      const line = parseInt(input.getAttribute('data-ajax-cart-quantity-input'), 10);
      const qty = Math.max(0, parseInt(input.value, 10) || 0);

      clearTimeout(this._qtyTimers.get(line));
      const timer = setTimeout(() => {
        this.change({line, quantity: qty}, {info: {initiator: input}});
      }, this._conf.quantityDebounce);
      this._qtyTimers.set(line, timer);
    }
  }

  if (!customElements.get('native-ajax-cart')) {
    customElements.define('native-ajax-cart', NativeAjaxCart);
  }

  // Guarantee the singleton exists even if the markup tag is missing.
  function ensureMounted() {
    if (document.querySelector('native-ajax-cart')) { return; }
    const el = document.createElement('native-ajax-cart');
    el.setAttribute('hidden', '');
    (document.body || document.documentElement).appendChild(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureMounted);
  } else {
    ensureMounted();
  }

  // Hoisted helpers for raw-fetch bundle/set ATC (miracle-balm sets, minis
  // builder, legacy bundle LPs). Refresh drawer HTML before opening so users
  // never see an empty drawer flash while sections re-render.
  window.JR = window.JR || {};

  function jrGetDrawerSectionId() {
    const drawer = document.querySelector('cart-drawer');
    const sectionEl = drawer?.closest('[id^="shopify-section-"]');
    return sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;
  }

  function jrApplyCartToChrome(cart) {
    if (!isFullCart(cart)) return;
    if (window.__cartJsCache?.set) window.__cartJsCache.set(cart);
    const count = cart.item_count || 0;
    document.querySelectorAll('[data-cart-count], [data-ajax-cart-bind="item_count"]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('hidec', count === 0);
      el.classList.toggle('showc', count > 0);
    });
    document.documentElement.classList.toggle('js-ajax-cart-empty', count === 0);
    document.documentElement.classList.toggle('js-ajax-cart-not-empty', count > 0);
  }

  function jrFetchCartAsync() {
    const root = window.Shopify?.routes?.root || '/';
    return fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}})
      .then((r) => r.json())
      .catch(() => null);
  }

  function jrRenderSectionsHtml(sectionsHtml) {
    if (!sectionsHtml || typeof sectionsHtml !== 'object') return;
    Object.entries(sectionsHtml).forEach(([sid, html]) => {
      if (!html) return;
      const sectionEl = document.getElementById(`shopify-section-${sid}`);
      if (!sectionEl) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      sectionEl.querySelectorAll('[data-ajax-cart-section]').forEach((liveEl) => {
        const fresh = doc.querySelector('[data-ajax-cart-section]');
        if (fresh) liveEl.innerHTML = fresh.innerHTML;
      });
    });
  }

  function jrRefreshDrawerSectionAsync() {
    const sid = jrGetDrawerSectionId();
    const drawer = document.querySelector('cart-drawer');
    const cartEl = drawer?.closest('[id^="shopify-section-"]')?.querySelector('[data-ajax-cart-section]');
    if (!sid || !cartEl) return Promise.resolve(false);

    return fetch(`${window.location.pathname}?sections=${encodeURIComponent(sid)}`)
      .then((r) => r.json())
      .then((data) => {
        const html = data?.[sid];
        if (!html) return false;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-ajax-cart-section]');
        if (fresh) cartEl.innerHTML = fresh.innerHTML;
        return true;
      })
      .catch(() => false);
  }

  function jrRefreshAjaxCartSectionsAsync() {
    const jobs = Array.from(document.querySelectorAll('[data-ajax-cart-section]')).map((cartEl) => {
      const section = cartEl.closest('[id^="shopify-section-"]');
      if (!section) return Promise.resolve();
      const sid = section.id.replace('shopify-section-', '');
      return fetch(`${window.location.pathname}?sections=${encodeURIComponent(sid)}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data[sid]) return;
          const doc = new DOMParser().parseFromString(data[sid], 'text/html');
          const fresh = doc.querySelector('[data-ajax-cart-section]');
          if (fresh) cartEl.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
    return Promise.all(jobs);
  }

  function jrOpenCartDrawer() {
    window.dispatchEvent(new CustomEvent('cart-open'));
  }

  if (typeof window.JR.refreshAjaxCartSections !== 'function') {
    window.JR.refreshAjaxCartSections = function refreshAjaxCartSections() {
      jrRefreshAjaxCartSectionsAsync();
    };
  }

  if (typeof window.JR.refreshCartThenOpen !== 'function') {
    window.JR.refreshCartThenOpen = function refreshCartThenOpen(passedCart) {
      const cartPromise = isFullCart(passedCart) ? Promise.resolve(passedCart) : jrFetchCartAsync();
      return Promise.all([cartPromise, jrRefreshDrawerSectionAsync()])
        .then(([cart]) => {
          if (isFullCart(cart)) jrApplyCartToChrome(cart);
          jrOpenCartDrawer();
        });
    };
  }

  if (typeof window.JR.addSetThenOpen !== 'function') {
    window.JR.addSetThenOpen = function addSetThenOpen(items) {
      const root = window.Shopify?.routes?.root || '/';
      const sid = jrGetDrawerSectionId();
      const body = {items};
      if (sid) {
        body.sections = sid;
        body.sections_url = window.location.pathname;
      }

      return fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify(body),
      })
        .then((res) => res.json().then((data) => ({ok: res.ok, data})))
        .then(({ok, data}) => {
          if (!ok) {
            const msg = data?.description || data?.message || 'Add to cart failed';
            throw new Error(msg);
          }
          if (data?.sections) jrRenderSectionsHtml(data.sections);
          return isFullCart(data) ? data : jrFetchCartAsync();
        })
        .then((cart) => {
          if (isFullCart(cart)) jrApplyCartToChrome(cart);
          jrOpenCartDrawer();
        });
    };
  }
})();
